import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Bell, BellOff, Check, CheckCheck, X } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface Notification {
    id: string;
    title: string;
    message: string;
    type: string;
    link: string;
    read: boolean;
    created_at: string;
}

export default function NotificationBell({ token }: { token: string }) {
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [enabled, setEnabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownDirection, setDropdownDirection] = useState<'right' | 'left'>('right');

    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const fetchUnreadCount = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/notifications/unread-count`, { headers });
            setUnreadCount(res.data.count || 0);
        } catch { /* silent */ }
    }, [headers]);

    const fetchNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/notifications`, { headers });
            setNotifications(res.data || []);
        } catch { /* silent */ }
        setLoading(false);
    }, [headers]);

    const fetchPreference = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/auth/me`, { headers });
            setEnabled(res.data.notifications_enabled !== false);
        } catch { /* silent */ }
    }, [headers]);

    useEffect(() => {
        fetchPreference();
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 30000);
        return () => clearInterval(interval);
    }, [fetchUnreadCount, fetchPreference]);

    useEffect(() => {
        if (open) fetchNotifications();
    }, [open, fetchNotifications]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Compute dropdown direction based on bell position
    useEffect(() => {
        if (open && dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            // If the bell is in the left half of the screen, open right; otherwise open left
            if (rect.left < window.innerWidth / 2) {
                setDropdownDirection('right');
            } else {
                setDropdownDirection('left');
            }
        }
    }, [open]);

    const markRead = async (id: string) => {
        await axios.post(`${API}/notifications/${id}/read`, {}, { headers });
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
    };

    const markAllRead = async () => {
        await axios.post(`${API}/notifications/mark-all-read`, {}, { headers });
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setUnreadCount(0);
    };

    const toggleNotifications = async () => {
        const newVal = !enabled;
        setEnabled(newVal);
        try {
            await axios.put(`${API}/users/notification-preference`, { notifications_enabled: newVal }, { headers });
            toast.success(newVal ? 'Notifications enabled' : 'Notifications disabled');
            if (!newVal) { setUnreadCount(0); setOpen(false); }
        } catch {
            setEnabled(!newVal);
            toast.error('Failed to update preference');
        }
    };

    const timeAgo = (dateStr: string) => {
        const diff = Date.now() - new Date(dateStr).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'just now';
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        return `${Math.floor(hrs / 24)}d ago`;
    };

    const typeIcon = (type: string) => {
        if (type === 'erx_incoming') return 'bg-violet-500/20 text-violet-400';
        if (type === 'erx_status') return 'bg-emerald-500/20 text-emerald-400';
        return 'bg-violet-500/20 text-violet-400';
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <Button
                variant="ghost"
                size="icon"
                className="relative h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-violet-500/10"
                onClick={() => setOpen(!open)}
                data-testid="notification-bell-btn"
            >
                {enabled ? <Bell className="h-5 w-5" /> : <BellOff className="h-5 w-5 opacity-50" />}
                {unreadCount > 0 && enabled && (
                    <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 flex items-center justify-center rounded-full bg-violet-500 text-[10px] font-bold text-white" data-testid="notification-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </Button>

            {open && (
                <div className={`absolute ${dropdownDirection === 'right' ? 'left-0' : 'right-0'} bottom-full mb-2 w-80 z-50 rounded-xl border border-violet-500/20 bg-card shadow-xl shadow-violet-950/30 backdrop-blur-xl overflow-hidden`} data-testid="notification-dropdown">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-violet-500/15">
                        <span className="text-sm font-semibold text-foreground">Notifications</span>
                        <div className="flex items-center gap-2">
                            {unreadCount > 0 && (
                                <button onClick={markAllRead} className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1" data-testid="mark-all-read-btn">
                                    <CheckCheck className="h-3 w-3" /> Mark all read
                                </button>
                            )}
                            <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {/* Toggle */}
                    <div className="flex items-center justify-between px-4 py-2 border-b border-violet-500/15 bg-muted/30">
                        <span className="text-xs text-muted-foreground">{enabled ? 'Notifications on' : 'Notifications off'}</span>
                        <Switch checked={enabled} onCheckedChange={toggleNotifications} data-testid="notification-toggle" />
                    </div>

                    {/* Notification List */}
                    <div className="max-h-80 overflow-y-auto">
                        {!enabled ? (
                            <div className="py-8 text-center text-sm text-muted-foreground" data-testid="notifications-disabled-msg">
                                <BellOff className="h-8 w-8 mx-auto mb-2 opacity-30" />
                                Notifications are turned off
                            </div>
                        ) : loading ? (
                            <div className="py-8 text-center text-sm text-muted-foreground">Loading...</div>
                        ) : notifications.length === 0 ? (
                            <div className="py-8 text-center text-sm text-muted-foreground" data-testid="no-notifications-msg">
                                <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
                                No notifications yet
                            </div>
                        ) : (
                            notifications.map(n => (
                                <div
                                    key={n.id}
                                    className={`flex items-start gap-3 px-4 py-3 border-b border-violet-500/10 transition-colors cursor-pointer hover:bg-violet-500/5 ${!n.read ? 'bg-violet-500/5' : ''}`}
                                    onClick={() => !n.read && markRead(n.id)}
                                    data-testid={`notification-item-${n.id}`}
                                >
                                    <div className={`mt-0.5 h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${typeIcon(n.type)}`}>
                                        <Bell className="h-4 w-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-medium text-foreground truncate">{n.title}</p>
                                            {!n.read && <span className="h-2 w-2 rounded-full bg-violet-500 flex-shrink-0" />}
                                        </div>
                                        <p className="text-xs text-muted-foreground truncate">{n.message}</p>
                                        <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</p>
                                    </div>
                                    {!n.read && (
                                        <button className="mt-1 text-muted-foreground hover:text-violet-400" title="Mark as read">
                                            <Check className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
